import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { extensionMsg, t } from '../../common/i18n/index.js';
import { findBestMatchForExtensionNumber } from '../../common/utils/pbxUtils.js';
import { Loading } from '../Loading/index.js';
import { NumberFetchingError } from './NumberFetchingError.js';
import type { NumberRangeData } from '../../common/loaders.js';
import type { CommonError } from '../../common/types/errors.js';
import type { NumberRange } from '../../generated/api/models.js';

interface ExtensionNumberProps {
  extensionNumberRanges: NumberRange[];
  updateExtensionRangeId: (newRangeId: string | undefined) => void;
  updateExtensionNumber: (newNumber: string | undefined) => void;
  selectedExtensionRangeId?: string;
  selectedExtensionNumber?: string;
  selectedCorporateNumber?: string;
  rangeMap: Record<string, NumberRangeData>;
  onRetryLoadRange: (rangeId: string) => void;
  blockedExtensionNumbers?: string[];
}

export const ExtensionNumberAndRangeContent = (props: ExtensionNumberProps) => {
  const {
    blockedExtensionNumbers,
    extensionNumberRanges,
    selectedExtensionNumber,
    selectedCorporateNumber,
    updateExtensionNumber,
    selectedExtensionRangeId,
    updateExtensionRangeId,
    rangeMap,
    onRetryLoadRange,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<CommonError[]>([]);
  const [extensionNumbers, setExtensionNumbers] = useState<string[]>([]);

  const getDisplayNumberRangeDisplayText = (numberRange: NumberRange) =>
    `${numberRange.startNumber} – ${numberRange.endNumber}`;

  useEffect(() => {
    if (!selectedExtensionRangeId) {
      return setIsLoading(true);
    }
    const selectedNumberRange = rangeMap[selectedExtensionRangeId];
    if (!selectedNumberRange) {
      return setIsLoading(true);
    }

    const { displayedNumbers, errors: numberRangeErrors } = selectedNumberRange;
    if (numberRangeErrors) {
      return setErrors(numberRangeErrors);
    }

    if (displayedNumbers) {
      const availableExtensionNumbers: string[] =
        displayedNumbers?.filter(
          number =>
            (Number(number) < 700 || Number(number) >= 900) &&
            !blockedExtensionNumbers?.filter(blocked => blocked !== selectedExtensionNumber)?.includes(number)
        ) || [];

      setExtensionNumbers(availableExtensionNumbers);
      setIsLoading(false);
    }
  }, [rangeMap, selectedExtensionRangeId, blockedExtensionNumbers, selectedExtensionNumber]);

  if (extensionNumberRanges.find(numberRange => numberRange.rangeId === selectedExtensionRangeId) === undefined) {
    return null;
  }

  const onSelectCorporateNumber = (number?: string) => {
    if (number) {
      const bestMatch = findBestMatchForExtensionNumber(number, extensionNumbers);
      updateExtensionNumber(bestMatch || extensionNumbers[0]);
    }
  };

  return (
    <div className={dsClass.MARGIN_TOP_4}>
      <div className={dsClass.FONT_SIZE_LARGE}>{t.UM9T('Extension numbers')}:</div>
      <div className="of-attach-pbx-content__sub-numbers">
        <div className="of-attach-pbx-content__sub-number">
          <CL.Dropdown
            key={selectedExtensionRangeId}
            items={extensionNumberRanges
              .map(numberRange => ({
                label: getDisplayNumberRangeDisplayText(numberRange),
                value: numberRange.rangeId,
              }))
              .sort((optionA, optionB) => {
                return optionA.value > optionB.value ? 1 : -1;
              })}
            label={t.ESVA('Extension number range')}
            aria-label={t.ESVA('Extension number range')}
            selectedValue={selectedExtensionRangeId}
            onValueChange={element => {
              const newValue = element.attributes.getNamedItem('data-value')?.value;
              updateExtensionRangeId(newValue);
              onSelectCorporateNumber(selectedCorporateNumber);
            }}
          />
        </div>
        {selectedExtensionRangeId && errors?.length ? (
          <NumberFetchingError errors={errors} retryAction={() => onRetryLoadRange(selectedExtensionRangeId)} />
        ) : null}
        {isLoading ? (
          <Loading topPadding={1} text />
        ) : (
          <div className="of-attach-pbx-content__sub-number">
            <CL.Dropdown
              key={selectedExtensionNumber}
              items={extensionNumbers.map(number => ({
                label: number,
                value: number,
                disabled: number === selectedExtensionNumber,
              }))}
              label={t.NO66(extensionMsg)}
              aria-label={t.NO66(extensionMsg)}
              selectedValue={selectedExtensionNumber}
              onValueChange={element => {
                updateExtensionNumber(element.attributes.getNamedItem('data-value')?.value);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
